import React, { useMemo, useState } from 'react';
import { Col, Row } from 'tea-component';
import '@/pages/opinion/assets/scss/pages/competitor/trendsAnalysis.scss';

import SimilarGames from '../../components/SimilarGames';
import MentionsSentiment from '../../components/MentionsSentiment';
import TopicChart from '../../components/TopicChart';
import useBatchDauRequest from '../../hooks/useBatchDauRequest';
import KeywordChart from '../../components/KeywordChart';
import ChannelChart from '../../components/ChannelChart';
import LanguageChart from '../../components/LanguageChart';
import RegionCountryChart from '../../components/RegionCountryChart';
import GameStoreChart from '../../components/GameStoreChart';
import Navigator from '@op/components/Navigator';
import { scrollToAnchorPoint } from '@/pages/opinion/utils';

const TrendsAnalysis = ({
  filter,
  gameInfo,
  activeTab,
  translateTopicFunc,
  scrollBody,
  selectNavigator,
  setSelectNavigator,
  options,
}: any) => {
  const [xAxisList, setXAxisList] = useState<any>([]);

  const { hasPcGame, hasConsoleGame, hasMobileGame } = useMemo(() => {
    let entityTypeList: Array<string> =
      xAxisList?.map((el: { entity_type: string }) => el?.entity_type) || [];
    const hasMobileGame = entityTypeList.includes('mobile');
    return {
      hasPcGame: entityTypeList.includes('pc'),
      hasConsoleGame: entityTypeList.includes('console'),
      hasMobileGame: hasMobileGame,
    };
  }, [xAxisList]);

  const commonParams = useMemo(() => {
    const games = xAxisList.map(
      ({ edition_unified_id, entity_type, id_type, release_time }: any) => ({
        edition_unified_id,
        entity_type,
        id_type,
        release_time,
      }),
    );
    if (games.length === 0) {
      return {};
    }
    return { ...(gameInfo || {}), ...(filter || {}), games };
  }, [gameInfo, xAxisList]);

  const { dau } = useBatchDauRequest({ params: commonParams });

  return (
    <div className="dbo-trends-analysis">
      <Navigator
        scrollBody={scrollBody}
        fixNavigatorShow={false}
        options={options}
        onChange={(curNavigator: any) => {
          setSelectNavigator(curNavigator);
          scrollToAnchorPoint(curNavigator, 'start');
        }}
        value={selectNavigator}
      ></Navigator>

      <SimilarGames
        setXAxisList={setXAxisList}
        filter={filter}
        gameInfo={gameInfo}
      />
      <div id="mentions" className="dbo-scroll-target">
        <MentionsSentiment xAxisList={xAxisList} commonParams={commonParams} />
      </div>
      <Row>
        <Col span={24} id="topic" className="dbo-scroll-target">
          <TopicChart
            dau={dau}
            commonParams={commonParams}
            activeTab={activeTab}
            xAxisList={xAxisList}
            isNoMobile={!hasMobileGame}
            transformFunc={translateTopicFunc}
          ></TopicChart>
        </Col>
        <Col span={24} id="keywords" className="dbo-scroll-target">
          <KeywordChart
            dau={dau}
            commonParams={commonParams}
            activeTab={activeTab}
            xAxisList={xAxisList}
            isNoMobile={!hasMobileGame}
          ></KeywordChart>
        </Col>
        <Col span={24} className="dbo-col dbo-scroll-target" id="gameStore">
          <GameStoreChart
            xAxisList={xAxisList}
            hasPcGame={hasPcGame}
            hasConsoleGame={hasConsoleGame}
            hasMobileGame={hasMobileGame}
            commonParams={commonParams}
          ></GameStoreChart>
        </Col>

        <Col span={24} id="channel" className="dbo-scroll-target">
          <ChannelChart
            dau={dau}
            commonParams={commonParams}
            activeTab={activeTab}
            xAxisList={xAxisList}
            isNoMobile={!hasMobileGame}
            hasSentiment={false}
          ></ChannelChart>
        </Col>
        <Col span={24} id="language" className="dbo-scroll-target">
          <LanguageChart
            dau={dau}
            commonParams={commonParams}
            activeTab={activeTab}
            xAxisList={xAxisList}
            isNoMobile={!hasMobileGame}
            hasSentiment={false}
          ></LanguageChart>
        </Col>
        <Col span={24} id="region" className="dbo-scroll-target">
          <RegionCountryChart
            dau={dau}
            commonParams={commonParams}
            activeTab={activeTab}
            xAxisList={xAxisList}
            isNoMobile={!hasMobileGame}
            hasSentiment={false}
          ></RegionCountryChart>
        </Col>
      </Row>
    </div>
  );
};

export default TrendsAnalysis;
